import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  TableBody,
  TextField,
  Typography,
} from '@mui/material';
import {
  CustomBodyCell,
  CustomHeaderCell,
  CustomTable,
  CustomTableContainer,
  CustomTableRow,
} from '@components/Styles/TableStyle';
import {
  CommentListItem,
  CustomAutocomplete,
  CustomLink,
  CustomStack,
  CustomTextField,
  RequiredMark,
  RequiredMarkBox,
  SectionWrapper,
} from '@components/Styles/CommonStyle';
import React, { forwardRef, useEffect, useState } from 'react';

import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ko from 'date-fns/locale/ko';
import { CustomButton } from '@components/Styles/ButtonStyle';
import api from '@api/axios';
import moment from 'moment';
import { theme } from '@components/Styles/theme';
import { useLoading } from '@context/LoadingContext';
import { useNavigate } from 'react-router-dom';
import { formatAmount } from '@utils/formatter';
import DatePicker from 'react-datepicker';
import CustomInputDate from '../Input/CustomInputDate';
import { useDialog } from '@context/DialogContext';

const PlannedProjectCreateTable = ({
  projectInfo,
  setProjectInfo,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  id,
  comment,
  setComment,
  commentList,
  setCommentList,
  commentListEditFlag,
  setCommentListEditFlag,
  searchOrderingCompanyList,
  searchAccountCompanyList,
}) => {
  const { showLoading, hideLoading } = useLoading();
  const { showDialog } = useDialog();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  // 댓글 등록
  const createComment = () => {
    const url = '/api/project/createPlannedProjectComment?id=' + id;

    // 댓글 등록 중복 방지
    if (isSubmitting) return;
    setIsSubmitting(true);

    showLoading();
    api
      .post(url, { comment: comment })
      .then((res) => {
        console.log('res');
        console.log(res);
        if (res.status === 201) {
          // 댓글 새로 불러오기
          readComment();
          setComment('');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        hideLoading();
        setIsSubmitting(false);
      });
  };

  // 댓글 삭제하기 (알림 호출)
  const deleteCommentAlert = (commentId) => {
    showDialog({
      type: 'confirm',
      title: '확인',
      message: '정말 삭제하시겠습니까?',
      onConfirm: () => {
        deleteComment(commentId);
      },
    });
  };

  // 댓글 삭제하기 (API 호출)
  const deleteComment = (commentId) => {
    const url = '/api/project/deletePlannedProjectComment?commentId=' + commentId;

    showLoading();
    api
      .delete(url)
      .then((res) => {
        console.log('댓글 삭제하기 :: res');
        console.log(res);
        if (res.status === 201) {
          readComment();
          setComment('');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        hideLoading();
      });
  };

  // 댓글 수정하기 (API 호출)
  const updateComment = (commentId, comment) => {
    const url = '/api/project/updatePlannedProjectComment';

    showLoading();
    api
      .put(url, { comment: comment, commentId: commentId })
      .then((res) => {
        console.log('댓글 수정하기 :: res');
        console.log(res);
        if (res.status === 201) {
          readComment();
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        hideLoading();
      });
  };

  // 댓글 불러오기 (API 호출)
  const readComment = () => {
    const url = '/api/project/readPlannedProjectComment?id=' + id;

    showLoading();
    api
      .get(url)
      .then((res) => {
        console.log('res');
        console.log(res);
        if (res.status === 200) {
          setCommentList(res.data.result);
          setCommentListEditFlag(res.data.result.map((item) => false));
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        hideLoading();
      });
  };

  // 확정사업 등록 (API 호출)
  const projectConfirm = () => {
    const url = '/api/project/updateProjectConfirm?id=' + id;

    showLoading();
    api
      .post(url, {})
      .then((res) => {
        console.log('res');
        console.log(res);
        if (res.status === 201) {
          // 사업현황 목록으로 이동
          navigate('/project/projectList');
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const CustomInput = forwardRef(({ value, onClick, onChange }, ref) => (
    <TextField
      fullWidth
      sx={{
        '& .MuiInputBase-root': {
          minHeight: '32px',
          padding: '0',
        },

        '& .MuiInputBase-input': {
          minHeight: '32px',
          padding: '4px 12px',
          boxSizing: 'border-box',
          fontSize: '15px',
          lineHeight: '1.6',
          color: '#444',
        },

        '& .MuiInputLabel-root': {
          lineHeight: '32px',
        },

        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: '#929292',
          backgroundColor: '#efefef',
        },

        '& .MuiButtonBase-root': {
          marginRight: '4px',
          color: '#000',
        },

        '& .MuiSvgIcon-root': {
          width: '14px',
          height: '14px',
        },
      }}
      value={value}
      onClick={onClick}
      onChange={onChange}
      inputRef={ref}
      placeholder="YYYY.MM.DD"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton>
              <CalendarTodayIcon />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  ));

  useEffect(() => {
    if (id) {
      readComment();
    }
  }, []);

  return (
    <React.Fragment>
      <CustomStack mb={2}>
        <Typography sx={theme.typography.h4}>예정 사업정보 등록</Typography>
      </CustomStack>
      <SectionWrapper>
        <CustomStack justifyContent="spaceBetween" mb={2}>
          <Typography sx={theme.typography.h4}>사업 정보</Typography>
          <RequiredMarkBox>
            <RequiredMark />
            (&nbsp;&nbsp;) 필수입력사항
          </RequiredMarkBox>
        </CustomStack>
        <CustomTableContainer>
          <CustomTable>
            <TableBody>
              <CustomTableRow>
                <CustomHeaderCell>
                  <RequiredMark />
                  사업명
                </CustomHeaderCell>
                <CustomBodyCell>
                  <CustomTextField
                    size="small"
                    value={projectInfo.name}
                    onChange={(e) => setProjectInfo({ ...projectInfo, name: e.target.value })}
                  />
                </CustomBodyCell>
                <CustomHeaderCell>
                  <RequiredMark />
                  예상시작일
                </CustomHeaderCell>
                <CustomBodyCell>
                  <DatePicker
                    locale={ko}
                    selected={startDate}
                    dateFormat="yyyy.M.d"
                    onChange={(date) => {
                      if (date) {
                        const formattedDate = moment(date).format('YYYY-MM-DD');
                        setStartDate(formattedDate);
                      } else {
                        setStartDate('');
                      }
                    }}
                    onChangeRaw={(event) => {
                      const inputValue = event.target.value;
                      const parsedDate = moment(inputValue, 'YYYY.M.D', true);
                      if (parsedDate.isValid()) {
                        const formattedDate = moment(parsedDate).format('YYYY-MM-DD');
                        setStartDate(formattedDate);
                      }
                    }}
                    customInput={<CustomInputDate />}
                  />
                </CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell>
                  <RequiredMark />
                  예상종료일
                </CustomHeaderCell>
                <CustomBodyCell>
                  <DatePicker
                    locale={ko}
                    selected={endDate}
                    onChange={(date) => {
                      if (date) {
                        const formattedDate = moment(date).format('YYYY-MM-DD');
                        setEndDate(formattedDate);
                      } else {
                        setEndDate('');
                      }
                    }}
                    dateFormat="yyyy.M.d"
                    onChangeRaw={(event) => {
                      const inputValue = event.target.value;
                      const parsedDate = moment(inputValue, 'YYYY.M.D', true);
                      if (parsedDate.isValid()) {
                        const formattedDate = moment(parsedDate).format('YYYY-MM-DD');
                        setEndDate(formattedDate);
                      }
                    }}
                    customInput={<CustomInputDate />}
                  />
                </CustomBodyCell>
                <CustomHeaderCell>
                  <RequiredMark />
                  예상규모 (VAT 별도)
                </CustomHeaderCell>
                <CustomBodyCell>
                  <CustomTextField
                    size="small"
                    value={formatAmount(projectInfo.amount)}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setProjectInfo({ ...projectInfo, amount: value });
                    }}
                  />
                </CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell>
                  <RequiredMark />
                  발주사
                </CustomHeaderCell>
                <CustomBodyCell>
                  <CustomAutocomplete
                    disableClearable
                    value={projectInfo.orderingCompany}
                    options={searchOrderingCompanyList}
                    onChange={(event, value) => {
                      if (value) {
                        setProjectInfo({ ...projectInfo, orderingCompany: value });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onChange={(e) => {
                          setProjectInfo({ ...projectInfo, orderingCompany: e.target.value });
                        }}
                      />
                    )}
                  />
                </CustomBodyCell>
                <CustomHeaderCell>
                  <RequiredMark />
                  거래처
                </CustomHeaderCell>
                <CustomBodyCell>
                  <CustomAutocomplete
                    disableClearable
                    value={projectInfo.accountCompany}
                    options={searchAccountCompanyList}
                    onChange={(event, value) => {
                      if (value) {
                        setProjectInfo({ ...projectInfo, accountCompany: value });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onChange={(e) => {
                          setProjectInfo({ ...projectInfo, accountCompany: e.target.value });
                        }}
                      />
                    )}
                  />
                </CustomBodyCell>
              </CustomTableRow>
              <CustomTableRow>
                <CustomHeaderCell>
                  <RequiredMark />
                  사업담당자
                </CustomHeaderCell>
                <CustomBodyCell colSpan={3}>
                  <CustomTextField
                    size="small"
                    value={projectInfo.projectManager}
                    onChange={(e) => setProjectInfo({ ...projectInfo, projectManager: e.target.value })}
                  />
                </CustomBodyCell>
              </CustomTableRow>
            </TableBody>
          </CustomTable>
        </CustomTableContainer>

        {id && (
          <CustomStack justifyContent="center" mt={2}>
            <CustomButton onClick={projectConfirm}>확정사업등록</CustomButton>
          </CustomStack>
        )}
      </SectionWrapper>

      {id && (
        <SectionWrapper data-section="comment">
          <CustomStack mb={2}>
            <Typography sx={theme.typography.h4}>진행현황</Typography>
          </CustomStack>

          <Box>
            {/* 댓글 등록 */}
            <Box>
              <CustomTextField
                multiline
                multi="12px 16px"
                rows={4}
                maxWidth="unset"
                placeholder="진행현황에 대하여 입력해주세요."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <CustomStack justifyContent="end" mt={2}>
                <CustomButton onClick={createComment}>등록</CustomButton>
              </CustomStack>
            </Box>

            {/* 댓글 목록 */}
            <Box>
              <List>
                {commentList && commentList.length > 0 ? (
                  commentList.map((item, index) =>
                    !commentListEditFlag[index] ? (
                      // 댓글 조회
                      <CommentListItem>
                        <Box sx={{ width: '100%' }}>
                          <CustomStack justifyContent="spaceBetween">
                            <CustomStack mb={2}>
                              <Typography sx={theme.typography.h6}>{item.created_user_name}</Typography>
                              <Typography sx={{ ...theme.typography.body1, color: theme.palette.gray.main }}>
                                {moment(item.created_at).format('YYYY-MM-DD')}
                              </Typography>
                            </CustomStack>
                            <CustomStack mb={2}>
                              <CustomLink
                                pr={16}
                                onClick={(e) => {
                                  e.preventDefault();
                                }}
                              >
                                <Typography
                                  sx={theme.typography.button}
                                  onClick={() => {
                                    setCommentListEditFlag(
                                      commentListEditFlag.map((item, itemIndex) => {
                                        if (index === itemIndex) {
                                          return !item;
                                        }
                                        return item;
                                      }),
                                    );
                                  }}
                                >
                                  수정
                                </Typography>
                              </CustomLink>
                              <CustomLink
                                onClick={(e) => {
                                  e.preventDefault();
                                }}
                              >
                                <Typography sx={theme.typography.button} onClick={() => deleteCommentAlert(item.id)}>
                                  삭제
                                </Typography>
                              </CustomLink>
                            </CustomStack>
                          </CustomStack>

                          <Typography sx={{ ...theme.typography.body1, whiteSpace: 'pre-line' }}>
                            {item.comment}
                          </Typography>
                        </Box>
                      </CommentListItem>
                    ) : (
                      // 댓글 수정
                      <CommentListItem>
                        <Box sx={{ width: '100%' }}>
                          <CustomStack justifyContent="spaceBetween">
                            <CustomStack mb={2}>
                              <Typography sx={theme.typography.h6}>{item.created_user_name}</Typography>
                              <Typography sx={{ ...theme.typography.body1, color: theme.palette.gray.main }}>
                                {moment(item.created_at).format('YYYY-MM-DD')}
                              </Typography>
                            </CustomStack>
                            <CustomStack mb={2}>
                              <CustomLink
                                pr={16}
                                onClick={(e) => {
                                  e.preventDefault();
                                }}
                              >
                                <Typography
                                  sx={theme.typography.button}
                                  onClick={() => {
                                    showDialog({
                                      type: 'confirm',
                                      title: '확인',
                                      message: '정말 수정하시겠습니까?',
                                      onConfirm: () => {
                                        updateComment(item.id, commentList[index].comment);
                                        setCommentListEditFlag(
                                          commentListEditFlag.map((item, itemIndex) => {
                                            if (index === itemIndex) {
                                              return !item;
                                            }
                                            return item;
                                          }),
                                        );
                                      },
                                    });
                                  }}
                                >
                                  수정
                                </Typography>
                              </CustomLink>
                              <CustomLink
                                onClick={(e) => {
                                  e.preventDefault();
                                }}
                              >
                                <Typography sx={theme.typography.button} onClick={() => deleteCommentAlert(item.id)}>
                                  삭제
                                </Typography>
                              </CustomLink>
                            </CustomStack>
                          </CustomStack>
                          <CustomTextField
                            multiline
                            multi="12px 16px"
                            rows={4}
                            maxWidth="unset"
                            placeholder="진행현황에 대하여 입력해주세요."
                            value={commentList[index].comment}
                            onChange={(e) => {
                              setCommentList(
                                commentList.map((item, itemIndex) => {
                                  if (index === itemIndex) {
                                    return { ...item, comment: e.target.value };
                                  }
                                  return item;
                                }),
                              );
                            }}
                          />
                        </Box>
                      </CommentListItem>
                    ),
                  )
                ) : (
                  <ListItem>
                    <ListItemText
                      sx={{ my: 6 }}
                      secondary={
                        <Typography
                          sx={{
                            ...theme.typography.body1,
                            whiteSpace: 'pre-line',
                            color: '#727272',
                            textAlign: 'center',
                          }}
                        >
                          등록된 댓글이 없습니다.
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        </SectionWrapper>
      )}
    </React.Fragment>
  );
};

export default PlannedProjectCreateTable;
